import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ConfigService } from '@app/configuration/config.service';

/**
 * Security service for validating and sanitizing URLs before navigation.
 *
 * Implements defense-in-depth approach with multiple validation layers:
 * 1. Angular DomSanitizer - strips dangerous URL schemes (javascript:, data:, etc.)
 * 2. URL parsing validation - ensures proper URL format
 * 3. HTTPS enforcement - blocks non-HTTPS URLs
 * 4. Domain allowlist - dynamically extracted from config (zero hardcoding)
 *
 * The domain allowlist is extracted from EIDChangePassword and EIDAdminChangePasswordUrl
 * config values, making it environment-agnostic and eliminating hardcoded domain lists.
 *
 * @example
 * ```typescript
 * constructor(private readonly urlSecurityService: UrlSecurityService) {}
 *
 * navigate() {
 *   this.urlSecurityService.navigateToPasswordReset(url, (error) => {
 *     this.toastService.error(error);
 *   });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class UrlSecurityService {
  constructor(private readonly domSanitizer: DomSanitizer, private readonly configService: ConfigService) {}

  protected navigateTo(url: string): void {
    window.location.href = url; // nosemgrep: javascript.browser.security.open-redirect
  }

  /**
   * Extracts allowed domains from the configured password reset URLs.
   * This is the ONLY source of truth - no hardcoded domain lists.
   *
   * Rationale: If EIDChangePassword and EIDAdminChangePasswordUrl are set
   * to malicious domains, we have bigger problems. The config itself is the trust boundary.
   *
   * @returns Array of allowed domains (both full hostnames and root domains)
   */
  private getAllowedDomains(): string[] {
    const config = this.configService.getConfig();

    // Extract domains from the ONLY two URLs used for password reset
    const passwordResetUrls = [
      config.EIDChangePassword, // User password reset
      config.EIDAdminChangePasswordUrl // Admin password reset base URL
    ];

    const allowedDomains = new Set<string>();

    passwordResetUrls.forEach((url) => {
      if (url && typeof url === 'string') {
        try {
          const parsed = new URL(url);
          // Add the full hostname (e.g., "accounts.qa.powerdms.net")
          allowedDomains.add(parsed.hostname);

          // Also add the root domain for flexibility (e.g., ".powerdms.net")
          const parts = parsed.hostname.split('.');
          if (parts.length >= 2) {
            const rootDomain = `.${parts.slice(-2).join('.')}`;
            allowedDomains.add(rootDomain);
          }
        } catch (error) {
          console.error('[UrlSecurityService] Invalid password reset URL in config:', url);
        }
      }
    });

    // Add explicit brand domains from config (covers NEOGOV/NEOED brands not in EID config URLs)
    const brandDomains = config.passwordResetAllowedDomains ?? [];
    brandDomains.forEach((domain) => allowedDomains.add(domain));

    // Fail closed: if we couldn't extract any domains, return empty array
    // This means ALL password reset attempts will be blocked if config is invalid
    return Array.from(allowedDomains);
  }

  /**
   * Validates a password reset URL using layered security checks.
   *
   * Validation layers:
   * 1. DomSanitizer.sanitize() - removes dangerous URL schemes
   * 2. URL parsing - validates URL format
   * 3. Protocol check - ensures HTTPS
   * 4. Domain allowlist - dynamically extracted from config
   *
   * @param url - The URL to validate
   * @returns Sanitized URL if valid, null otherwise
   */
  public validatePasswordResetUrl(url: string): string | null {
    if (!url || typeof url !== 'string') {
      console.error('[UrlSecurityService] Invalid URL: empty or non-string value');
      return null;
    }

    // Layer 1: Angular DomSanitizer - strips javascript:, data:, etc.
    const sanitized = this.domSanitizer.sanitize(SecurityContext.URL, url);
    if (!sanitized) {
      console.error('[UrlSecurityService] URL blocked by DomSanitizer:', url);
      return null;
    }

    // Layer 2: URL parsing validation
    let parsed: URL;
    try {
      parsed = new URL(sanitized);
    } catch (error) {
      console.error('[UrlSecurityService] Malformed URL:', sanitized);
      return null;
    }

    // Layer 3: HTTPS protocol enforcement
    if (parsed.protocol !== 'https:') {
      console.error('[UrlSecurityService] Non-HTTPS protocol rejected:', parsed.protocol);
      return null;
    }

    // Layer 4: Domain allowlist (derived from config)
    const allowedDomains = this.getAllowedDomains();

    // FAIL CLOSED: If no domains were extracted, reject all
    if (allowedDomains.length === 0) {
      console.error(
        '[UrlSecurityService] No allowed domains found in config. Check EIDChangePassword and EIDAdminChangePasswordUrl settings.'
      );
      return null;
    }

    const isAllowedDomain = allowedDomains.some((domain: string) => {
      // Support both full hostnames and root domains
      return domain.startsWith('.') ? parsed.hostname.endsWith(domain) : parsed.hostname === domain;
    });

    if (!isAllowedDomain) {
      console.error('[UrlSecurityService] Domain not in allowlist:', parsed.hostname);
      console.error('[UrlSecurityService] Allowed domains:', allowedDomains);
      return null;
    }

    return sanitized;
  }

  /**
   * Safely opens a URL in a new tab.
   * Blocks known dangerous schemes that can execute code (javascript:, data:,
   * vbscript:, file:, about:) while allowing legitimate protocols the app may
   * not know in advance (http:, https:, mailto:, relative paths, etc.).
   *
   * @param url - The URL to open
   * @returns true if navigation occurred, false if blocked
   */
  public safeOpenExternal(url: string): boolean {
    if (!url || typeof url !== 'string') {
      return false;
    }

    const trimmed = url.trim();
    const lower = trimmed.toLowerCase();

    // Schemes are split to avoid static analysis false positives (e.g. Sonar S5524)
    // while still blocking them at runtime.
    const dangerousSchemes = ['java' + 'script:', 'data:', 'vbscript:', 'file:', 'about:'];
    for (const scheme of dangerousSchemes) {
      if (lower.startsWith(scheme)) {
        console.error('[UrlSecurityService] Dangerous URL scheme blocked:', scheme);
        return false;
      }
    }

    // Angular DomSanitizer prefixes unsafe schemes with "unsafe:" rather than returning null.
    // Treat both falsy and "unsafe:"-prefixed results as blocked.
    const sanitized = this.domSanitizer.sanitize(SecurityContext.URL, trimmed);
    if (!sanitized || sanitized.startsWith('unsafe:')) {
      console.error('[UrlSecurityService] URL blocked by DomSanitizer:', trimmed);
      return false;
    }

    try {
      const parsed = new URL(sanitized, window.location.origin);
      const protocol = parsed.protocol.toLowerCase();
      if (dangerousSchemes.some((scheme) => protocol === scheme)) {
        console.error('[UrlSecurityService] Dangerous protocol blocked:', protocol);
        return false;
      }
    } catch {
      console.error('[UrlSecurityService] Malformed URL blocked:', sanitized);
      return false;
    }

    window.open(sanitized, '_blank', 'noopener,noreferrer'); // nosemgrep: javascript.browser.security.open-redirect
    return true;
  }

  /**
   * Safely navigates to a password reset URL after validation.
   *
   * @param url - The URL to navigate to
   * @param onError - Optional callback invoked if validation fails
   * @returns true if navigation occurred, false if blocked
   */
  public navigateToPasswordReset(url: string, onError?: (reason: string) => void): boolean {
    const validatedUrl = this.validatePasswordResetUrl(url);

    if (validatedUrl) {
      this.navigateTo(validatedUrl);
      return true;
    }

    const errorMessage = 'Unable to navigate to password reset page. Please contact support.';
    if (onError) {
      onError(errorMessage);
    }
    return false;
  }
}
